/* eslint-disable indent */
import React, { useEffect, useState, useCallback } from "react";
import { BarChart, Bar, YAxis, ResponsiveContainer, Tooltip, XAxis, LabelList } from "recharts";
import { useAuthContext } from "../../context/auth";
import { getOportunidades, getIntermediarios, getCategorias, getClientes } from "../../utils";
import { getConvertedImporte } from "../../utils/calculateVentas"; // Import utility function
import "./stylesVentasTop5.scss";

export const VentasTop5 = ({ filtroAno, showInDollars }) => {
  const [oportunidades, setOportunidades] = useState([]);
  const [intermediarios, setIntermediarios] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { pais } = useAuthContext();

  const localString = useCallback(
    (pais) =>
      ({
        Chile: "es-CL",
        Perú: "es-PE",
        Uruguay: "es-UY",
      })[pais] || "es-UY",
    []
  );

  const moneda = useCallback(
    (pais) =>
      ({
        Chile: "$ ",
        Perú: "S/ ",
        Uruguay: "$ ",
      })[pais] || "$ ",
    []
  );

  const tiposDeCambio = {
    Perú: 3.75,
    Chile: 950,
    Uruguay: 1, // Ajustado para Uruguay si no hay conversión
  };
  const rate = tiposDeCambio[pais] || 1;

  const getMoneda = useCallback(() => (showInDollars ? "US$ " : moneda(pais)), [showInDollars, pais]);
  const getLocale = useCallback(() => (showInDollars ? "en-US" : localString(pais)), [showInDollars, pais]);
  const getDisplayValue = useCallback(
    (value) => {
      const convertedValue = showInDollars ? value / rate : value;
      return convertedValue.toLocaleString(getLocale(), {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
    [showInDollars, rate, getLocale]
  );

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [newOportunidades, intermediariosData, categoriasData, clientesData] = await Promise.all([
          getOportunidades({ pais }),
          getIntermediarios({ pais }),
          getCategorias(),
          getClientes({ pais }),
        ]);
        setOportunidades(newOportunidades);
        setIntermediarios(intermediariosData || []);
        setCategorias(categoriasData || []);
        setClientes(clientesData || []);
        setError(null);
      } catch {
        setError("Error al cargar los datos. Por favor, intenta de nuevo.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [pais]);

  const calculateTopData = (data, key) => {
    if (data.length === 0 || intermediarios.length === 0 || categorias.length === 0 || clientes.length === 0) return [];

    // Filtramos las oportunidades para estatus "100%" y el año seleccionado
    const filteredOportunidades = data.filter(
      (oportunidad) =>
        oportunidad.estatus === "100%" &&
        parseInt(oportunidad.ano) === parseInt(filtroAno) &&
        !oportunidad.eliminada &&
        oportunidad.pais === pais
    );

    const uniqueOportunidades = [...new Map(filteredOportunidades.map((item) => [item.id, item])).values()];

    // Agrupamos por el key seleccionado
    const grouped = uniqueOportunidades.reduce((acc, item) => {
      let identifier;
      const clienteKey = item.client_id ? `${item.client_id}_${pais}` : null;
      const clienteData = clienteKey ? clientes.find((cli) => `${cli.id}_${cli.pais}` === clienteKey) : null;

      if (key === "cliente") {
        identifier = item.cliente?.trim().toUpperCase() || "DESCONOCIDO";
      } else if (key === "intermediario") {
        identifier =
          clienteData && clienteData.agencia && intermediarios.find((int) => int.id === clienteData.agencia)
            ? intermediarios
                .find((int) => int.id === clienteData.agencia)
                .nombre.trim()
                .toUpperCase()
            : "DESCONOCIDO";
      } else if (key === "categoria") {
        identifier =
          clienteData && clienteData.categoria && categorias.find((cat) => cat.id === clienteData.categoria)
            ? categorias
                .find((cat) => cat.id === clienteData.categoria)
                .nombre.trim()
                .toUpperCase()
            : "DESCONOCIDO";
      }

      if (identifier) {
        acc[identifier] = (acc[identifier] || 0) + getConvertedImporte(item, rate);
      }
      return acc;
    }, {});

    return Object.entries(grouped)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10); // Top 10
  };

  const clientesData = calculateTopData(oportunidades, "cliente");
  const agenciasData = calculateTopData(oportunidades, "intermediario");
  const categoriasData = calculateTopData(oportunidades, "categoria");

  const chartStyle = {
    fontFamily: "Trebuchet MS",
    fontSize: "10px",
  };

  const colors = {
    cliente: "#2E7D32",
    intermediario: "#1976D2",
    categoria: "#D81B60",
    clienteDark: "#072A0D",
    intermediarioDark: "#051B3F",
    categoriaDark: "#4C0526",
  };

  const getTextWidth = (text, fontSize, font) => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    context.font = `${fontSize} ${font}`;
    return context.measureText(text).width;
  };

  const customTick = ({ x, y, payload }) => {
    const text = payload.value;
    const lineHeight = 12;
    const maxWidth = 150;
    const words = text.split(" ");
    let line = "";
    const lines = [];
    for (let n = 0; n < words.length; n++) {
      const testLine = line + words[n] + " ";
      const testWidth = getTextWidth(testLine, chartStyle.fontSize, chartStyle.fontFamily);
      if (testWidth > maxWidth && n > 0) {
        lines.push(line.trim());
        line = words[n] + " ";
      } else {
        line = testLine;
      }
    }
    lines.push(line.trim());
    const dy = y + (lines.length * lineHeight) / 2 - lineHeight;
    return (
      <g transform={`translate(${x},${dy})`}>
        {lines.map((lineText, index) => (
          <text key={index} x={0} y={index * lineHeight} textAnchor="start" style={{ ...chartStyle, fill: "black" }}>
            {lineText}
          </text>
        ))}
      </g>
    );
  };

  const renderCustomLabel = (props) => {
    const { x, y, width, height, value, name } = props;
    if (value === undefined || typeof value !== "number") return null;
    const text = `${name} ${getMoneda()}${getDisplayValue(value)}`;
    const adjustedX = x + 50;
    const barEnd = x + width;
    const getTextColor = (charX) => (charX < barEnd ? "white" : "black");
    const textHeight = 12;
    const dy = (height - textHeight) / 3 + textHeight * 0.05;
    return (
      <g>
        {[...text].map((char, index) => {
          const prevCharsWidth = getTextWidth(text.slice(0, index), "15px", "Trebuchet MS");
          const charX = adjustedX + prevCharsWidth;
          const charWidth = getTextWidth(char, "15px", "Trebuchet MS");
          return (
            <text
              key={index}
              x={charX}
              y={y + height / 2}
              dy={dy}
              fontSize="12px"
              fill={getTextColor(charX + charWidth / 2)}
            >
              {char}
            </text>
          );
        })}
      </g>
    );
  };

  if (isLoading) return <div style={{ textAlign: "center" }}>Cargando...</div>;
  if (error) return <div style={{ color: "red", textAlign: "center" }}>{error}</div>;

  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", marginBottom: "20px" }}>
      <div
        style={{ width: "33%", marginRight: "10px", backgroundColor: "#E0E0E0", padding: "10px", borderRadius: "6px" }}
      >
        <h5
          style={{
            backgroundColor: colors.clienteDark,
            color: "white",
            padding: "10px",
            borderRadius: "6px",
            textAlign: "center",
          }}
        >
          CLIENTES
        </h5>
        <ResponsiveContainer width="100%" height={clientesData.length * 40 || 200}>
          <BarChart layout="vertical" data={clientesData} margin={{ left: -40, right: 0, top: 0, bottom: 0 }}>
            <XAxis type="number" hide={true} />
            <YAxis
              type="category"
              dataKey="name"
              width={60}
              interval={0}
              tickSize={0}
              tick={customTick}
              axisLine={{ x: 0 }}
              hide={true}
            />
            <Tooltip formatter={(value) => `${getMoneda()}${getDisplayValue(value)}`} />
            <Bar dataKey="value" fill={colors.cliente} radius={4} barSize={25}>
              <LabelList content={renderCustomLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div
        style={{ width: "33%", marginRight: "10px", backgroundColor: "#E0E0E0", padding: "10px", borderRadius: "6px" }}
      >
        <h5
          style={{
            backgroundColor: colors.intermediarioDark,
            color: "white",
            padding: "10px",
            borderRadius: "6px",
            textAlign: "center",
          }}
        >
          AGENCIAS
        </h5>
        <ResponsiveContainer width="100%" height={agenciasData.length * 40 || 200}>
          <BarChart layout="vertical" data={agenciasData} margin={{ left: -40, right: 0, top: 0, bottom: 0 }}>
            <XAxis type="number" hide={true} />
            <YAxis
              type="category"
              dataKey="name"
              width={60}
              interval={0}
              tickSize={0}
              tick={customTick}
              axisLine={{ x: 0 }}
              hide={true}
            />
            <Tooltip formatter={(value) => `${getMoneda()}${getDisplayValue(value)}`} />
            <Bar dataKey="value" fill={colors.intermediario} radius={4} barSize={25}>
              <LabelList content={renderCustomLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div
        style={{ width: "33%", marginRight: "10px", backgroundColor: "#E0E0E0", padding: "10px", borderRadius: "6px" }}
      >
        <h5
          style={{
            backgroundColor: colors.categoriaDark,
            color: "white",
            padding: "10px",
            borderRadius: "6px",
            textAlign: "center",
          }}
        >
          CATEGORÍAS
        </h5>
        <ResponsiveContainer width="100%" height={categoriasData.length * 40 || 200}>
          <BarChart layout="vertical" data={categoriasData} margin={{ left: -40, right: 0, top: 0, bottom: 0 }}>
            <XAxis type="number" hide={true} />
            <YAxis
              type="category"
              dataKey="name"
              width={60}
              interval={0}
              tickSize={0}
              tick={customTick}
              axisLine={{ x: 0 }}
              hide={true}
            />
            <Tooltip formatter={(value) => `${getMoneda()}${getDisplayValue(value)}`} />
            <Bar dataKey="value" fill={colors.categoria} radius={4} barSize={25}>
              <LabelList content={renderCustomLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
